/**
 * Homework Service
 * Handles all homework-related operations including class management, teacher management, 
 * student management, timetable system, and homework assignment management
 */

import { supabaseClient, supabaseAdmin } from '../../supabase/clients.js';

class HomeworkService {
  constructor() {
    this.client = supabaseClient;
    this.admin = supabaseAdmin;
  }

  /**
   * Get all classes for homework management
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of class objects
   */
  async getClasses(tenantId = 'tn_01') {
    try {
      // Try to get real class data
      try {
        const { data, error } = await this.client
          .from('saas_classes')
          .select(`
            id,
            class_id,
            class_name,
            class_level,
            class_section,
            class_master,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('class_level', { ascending: true })
          .order('class_section', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_classes table not found, generating mock data');
      }

      // Fallback: Generate mock class data
      return this.generateMockClasses();
    } catch (error) {
      console.error('Error fetching classes:', error);
      return this.generateMockClasses();
    }
  }

  /**
   * Get all teachers for homework management
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of teacher objects
   */
  async getTeachers(tenantId = 'tn_01') {
    try {
      // Try to get real teacher data
      try {
        const { data, error } = await this.client
          .from('saas_teachers')
          .select(`
            id,
            teacher_name,
            teacher_id,
            subjects,
            email,
            phone,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('teacher_name', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_teachers table not found, generating mock data');
      }

      // Fallback: Generate mock teacher data
      return this.generateMockTeachers();
    } catch (error) {
      console.error('Error fetching teachers:', error);
      return this.generateMockTeachers();
    }
  }

  /**
   * Get all students for homework management
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of student objects
   */
  async getStudents(tenantId = 'tn_01') {
    try {
      // Try to get real student data
      try {
        const { data, error } = await this.client
          .from('saas_students')
          .select(`
            id,
            student_name,
            student_id,
            class_id,
            elective_subjects,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('class_id', { ascending: true })
          .order('student_name', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_students table not found, generating mock data');
      }

      // Fallback: Generate mock student data
      return this.generateMockStudents();
    } catch (error) {
      console.error('Error fetching students:', error);
      return this.generateMockStudents();
    }
  }

  /**
   * Get class timetable with homework integration
   * @param {string} classId - Class identifier
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Timetable object with homework data
   */
  async getClassTimetable(classId, tenantId = 'tn_01') {
    try {
      // Try to get real timetable data
      try {
        const { data, error } = await this.client
          .from('saas_timetables')
          .select(`
            id,
            class_id,
            day_of_week,
            time_slot,
            subject,
            teacher_id,
            classroom,
            subject_type,
            elective_group,
            tenant_id,
            saas_teachers!inner(
              id,
              teacher_name,
              subjects
            )
          `)
          .eq('class_id', classId)
          .eq('tenant_id', tenantId)
          .order('day_of_week', { ascending: true })
          .order('time_slot', { ascending: true });

        if (!error && data && data.length > 0) {
          return this.transformTimetableData(data);
        }
      } catch (tableError) {
        console.log('saas_timetables table not found, generating mock data');
      }

      // Fallback: Generate mock timetable data
      return this.generateMockTimetable(classId);
    } catch (error) {
      console.error('Error fetching class timetable:', error);
      return this.generateMockTimetable(classId);
    }
  }

  /**
   * Get homework assignments for a specific class and date
   * @param {string} classId - Class identifier
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of homework objects
   */
  async getHomeworkAssignments(classId, date, tenantId = 'tn_01') {
    try {
      // Try to get real homework data
      try {
        const { data, error } = await this.client
          .from('saas_homework_assignments')
          .select(`
            id,
            class_id,
            subject,
            teacher_id,
            assignment_title,
            assignment_description,
            assignment_type,
            assigned_date,
            due_date,
            priority_level,
            completion_status,
            related_course_id,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('class_id', classId)
          .eq('assigned_date', date)
          .eq('tenant_id', tenantId)
          .order('due_date', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_homework_assignments table not found, generating mock data');
      }

      // Fallback: Generate mock homework data
      return this.generateMockHomework(classId, date);
    } catch (error) {
      console.error('Error fetching homework assignments:', error);
      return this.generateMockHomework(classId, date);
    }
  }

  /**
   * Get homework assignments for a specific date range
   * @param {string} classId - Class identifier
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of homework objects
   */
  async getHomeworkByDateRange(classId, startDate, endDate, tenantId = 'tn_01') {
    try {
      // Try to get real homework data
      try {
        const { data, error } = await this.client
          .from('saas_homework_assignments')
          .select(`
            id,
            class_id,
            subject,
            teacher_id,
            assignment_title,
            assignment_description,
            assignment_type,
            assigned_date,
            due_date,
            priority_level,
            completion_status,
            related_course_id,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('class_id', classId)
          .gte('assigned_date', startDate)
          .lte('assigned_date', endDate)
          .eq('tenant_id', tenantId)
          .order('assigned_date', { ascending: true })
          .order('due_date', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_homework_assignments table not found, generating mock data');
      }

      // Fallback: Generate mock homework data for date range
      return this.generateMockHomeworkForDateRange(classId, startDate, endDate);
    } catch (error) {
      console.error('Error fetching homework by date range:', error);
      return this.generateMockHomeworkForDateRange(classId, startDate, endDate);
    }
  }

  /**
   * Get homework statistics
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Statistics object
   */
  async getHomeworkStats(tenantId = 'tn_01') {
    try {
      const [classes, teachers, students] = await Promise.all([
        this.getClasses(tenantId),
        this.getTeachers(tenantId),
        this.getStudents(tenantId)
      ]);

      const totalClasses = classes.length;
      const totalTeachers = teachers.length;
      const totalStudents = students.length;
      const electiveClasses = classes.filter(cls => cls.class_level >= 6).length;
      const electiveStudents = students.filter(student => 
        student.elective_subjects && student.elective_subjects.length > 0
      ).length;

      return {
        totalClasses,
        totalTeachers,
        totalStudents,
        electiveClasses,
        electiveStudents,
        classLevels: [...new Set(classes.map(c => c.class_level))].sort(),
        subjects: [...new Set(teachers.flatMap(t => t.subjects || []))].sort()
      };
    } catch (error) {
      console.error('Error fetching homework stats:', error);
      return {
        totalClasses: 0,
        totalTeachers: 0,
        totalStudents: 0,
        electiveClasses: 0,
        electiveStudents: 0,
        classLevels: [],
        subjects: []
      };
    }
  }

  /**
   * Transform timetable data into the required format
   * @param {Array} data - Raw timetable data
   * @returns {Object} Transformed timetable data
   */
  transformTimetableData(data) {
    const timetable = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: []
    };

    const dayMap = {
      1: 'monday',
      2: 'tuesday', 
      3: 'wednesday',
      4: 'thursday',
      5: 'friday'
    };

    data.forEach(item => {
      const day = dayMap[item.day_of_week];
      if (day && timetable[day]) {
        timetable[day][item.time_slot - 1] = {
          subject: item.subject,
          teacher: item.saas_teachers?.teacher_name || 'Unknown Teacher',
          classroom: item.classroom || 'TBA',
          type: item.subject_type || 'core',
          electiveGroup: item.elective_group
        };
      }
    });

    return timetable;
  }

  /**
   * Generate mock class data
   * @returns {Array} Array of mock class objects
   */
  generateMockClasses() {
    const classes = [];
    const classLevels = [1, 2, 3, 4, 5, 6];
    const sections = ['A', 'B', 'C', 'D'];

    classLevels.forEach(level => {
      sections.forEach(section => {
        const classId = `${level}${section}`;
        const className = `中${level}${section}`;
        
        classes.push({
          id: `class_${classId}`,
          class_id: classId,
          class_name: className,
          class_level: level,
          class_section: section,
          class_master: this.getMockClassMaster(level, section),
          tenant_id: 'tn_01',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      });
    });

    return classes;
  }

  /**
   * Generate mock teacher data
   * @returns {Array} Array of mock teacher objects
   */
  generateMockTeachers() {
    const teachers = [
      { name: '王老師', subjects: ['中國語文', '中國文學'] },
      { name: '李老師', subjects: ['英國語文', '英語'] },
      { name: '張老師', subjects: ['數學', '數學延伸'] },
      { name: '陳老師', subjects: ['通識教育', '公民與社會發展'] },
      { name: '劉老師', subjects: ['物理', '化學'] },
      { name: '黃老師', subjects: ['化學', '生物'] },
      { name: '林老師', subjects: ['生物', '地理'] },
      { name: '吳老師', subjects: ['歷史', '地理'] },
      { name: '鄭老師', subjects: ['經濟', '企業會計與財務概論'] },
      { name: '周老師', subjects: ['體育', '音樂'] },
      { name: '蔡老師', subjects: ['視覺藝術', '設計與應用科技'] }
    ];

    return teachers.map((teacher, index) => ({
      id: `teacher_${index + 1}`,
      teacher_name: teacher.name,
      teacher_id: `T${(index + 1).toString().padStart(3, '0')}`,
      subjects: teacher.subjects,
      email: `${teacher.name.toLowerCase().replace('老師', '')}@school.edu.hk`,
      phone: `+852-${Math.floor(Math.random() * 90000000) + 20000000}`,
      tenant_id: 'tn_01',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
  }

  /**
   * Generate mock student data
   * @returns {Array} Array of mock student objects
   */
  generateMockStudents() {
    const students = [];
    const classLevels = [1, 2, 3, 4, 5, 6];
    const sections = ['A', 'B', 'C', 'D'];
    const studentNames = [
      '張小明', '李美華', '王大偉', '陳小芳', '劉志強', '黃淑芬', '林建國', '吳雅婷',
      '鄭志明', '周美玲', '蔡文華', '許志強', '謝淑娟', '羅建民', '葉雅文', '何志豪',
      '高美惠', '梁文傑', '蘇志明', '范淑芬', '江建華', '方雅婷', '石志強', '余美玲',
      '鄧文傑'
    ];

    classLevels.forEach(level => {
      sections.forEach(section => {
        const classId = `${level}${section}`;
        
        // Generate 25 students per class
        for (let i = 1; i <= 25; i++) {
          const nameIndex = (level * 4 + section.charCodeAt(0) - 65 + i - 1) % studentNames.length;
          const studentId = `${classId}-${i.toString().padStart(2, '0')}`;
          
          let electiveSubjects = [];
          if (level >= 6) {
            // Form 6 students have elective subjects
            const x1Subjects = ['生物', '化學', '物理', '歷史', '地理'];
            const x2Subjects = ['生物', '化學', '物理', '歷史', '數學延伸'];
            
            electiveSubjects = [
              x1Subjects[Math.floor(Math.random() * x1Subjects.length)],
              x2Subjects[Math.floor(Math.random() * x2Subjects.length)]
            ];
          }
          
          students.push({
            id: `student_${studentId}`,
            student_name: studentNames[nameIndex],
            student_id: studentId,
            class_id: classId,
            elective_subjects: electiveSubjects,
            tenant_id: 'tn_01',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        }
      });
    });

    return students;
  }

  /**
   * Generate mock timetable data
   * @param {string} classId - Class identifier
   * @returns {Object} Mock timetable data
   */
  generateMockTimetable(classId) {
    const classLevel = parseInt(classId.charAt(0));
    const timeSlots = 10;
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    
    const coreSubjects = ['中國語文', '英國語文', '數學', '通識教育'];
    const scienceSubjects = ['物理', '化學', '生物'];
    const humanitiesSubjects = ['歷史', '地理', '經濟'];
    
    const timetable = {};
    
    days.forEach(day => {
      timetable[day] = [];
      
      for (let slot = 0; slot < timeSlots; slot++) {
        let subject, teacher, classroom, type = 'core';
        
        if (classLevel >= 6) {
          // Form 6 - include electives
          const subjectIndex = (days.indexOf(day) * 10 + slot) % 12;
          if (subjectIndex < 4) {
            subject = coreSubjects[subjectIndex % coreSubjects.length];
            teacher = this.getMockTeacher(subject);
            classroom = this.getMockClassroom(subject);
          } else if (subjectIndex < 7) {
            subject = scienceSubjects[subjectIndex % scienceSubjects.length];
            teacher = this.getMockTeacher(subject);
            classroom = this.getMockClassroom(subject);
            type = 'elective';
          } else {
            subject = humanitiesSubjects[subjectIndex % humanitiesSubjects.length];
            teacher = this.getMockTeacher(subject);
            classroom = this.getMockClassroom(subject);
            type = 'elective';
          }
        } else {
          // Lower forms - core subjects only
          const subjectIndex = (days.indexOf(day) * 10 + slot) % 6;
          if (subjectIndex < 4) {
            subject = coreSubjects[subjectIndex % coreSubjects.length];
            teacher = this.getMockTeacher(subject);
            classroom = this.getMockClassroom(subject);
          } else {
            subject = scienceSubjects[subjectIndex % scienceSubjects.length];
            teacher = this.getMockTeacher(subject);
            classroom = this.getMockClassroom(subject);
          }
        }
        
        timetable[day][slot] = {
          subject,
          teacher,
          classroom,
          type
        };
      }
    });
    
    return timetable;
  }

  /**
   * Generate mock homework assignments
   * @param {string} classId - Class identifier
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Array} Array of mock homework objects
   */
  generateMockHomework(classId, date) {
    const subjects = ['中國語文', '英國語文', '數學', '物理', '化學', '生物', '歷史', '地理', '經濟'];
    const homeworkTypes = ['練習', '作文', '實驗報告', '研究報告', '背誦', '繪圖'];
    const homework = [];
    
    // Generate 2-4 homework assignments per day
    const assignmentCount = Math.floor(Math.random() * 3) + 2;
    
    for (let i = 0; i < assignmentCount; i++) {
      const subject = subjects[Math.floor(Math.random() * subjects.length)];
      const assignmentType = homeworkTypes[Math.floor(Math.random() * homeworkTypes.length)];
      const assignedDate = new Date(date);
      const dueDate = new Date(assignedDate);
      dueDate.setDate(dueDate.getDate() + Math.floor(Math.random() * 7) + 1);
      
      const priorities = ['high', 'medium', 'low'];
      const priority = priorities[Math.floor(Math.random() * priorities.length)];
      
      homework.push({
        id: `homework_${classId}_${date}_${i}`,
        class_id: classId,
        subject: subject,
        teacher_id: this.getMockTeacherId(this.getMockTeacher(subject)),
        assignment_title: this.getMockHomeworkTitle(subject, assignmentType),
        assignment_description: this.getMockHomeworkDescription(subject, assignmentType),
        assignment_type: assignmentType,
        assigned_date: date,
        due_date: dueDate.toISOString().split('T')[0],
        priority_level: priority,
        completion_status: Math.random() > 0.7 ? 'completed' : 'pending',
        related_course_id: `course_${classId}_${date}_${i}`,
        tenant_id: 'tn_01',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    }
    
    return homework;
  }

  /**
   * Generate mock homework assignments for date range
   * @param {string} classId - Class identifier
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Array} Array of mock homework objects
   */
  generateMockHomeworkForDateRange(classId, startDate, endDate) {
    const homework = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const dateStr = date.toISOString().split('T')[0];
      const dayOfWeek = date.getDay();
      
      // Skip weekends
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        const dailyHomework = this.generateMockHomework(classId, dateStr);
        homework.push(...dailyHomework);
      }
    }
    
    return homework;
  }

  // Helper methods for mock data generation
  getMockClassMaster(level, section) {
    const masters = ['王老師', '李老師', '張老師', '陳老師', '劉老師', '黃老師'];
    return masters[(level * 4 + section.charCodeAt(0) - 65) % masters.length];
  }

  getMockTeacher(subject) {
    const teacherMap = {
      '中國語文': '王老師', '英國語文': '李老師', '數學': '張老師', '通識教育': '陳老師',
      '物理': '劉老師', '化學': '黃老師', '生物': '林老師', '歷史': '吳老師',
      '地理': '鄭老師', '經濟': '周老師', '體育': '蔡老師', '音樂': '許老師',
      '視覺藝術': '謝老師', '設計與應用科技': '羅老師'
    };
    return teacherMap[subject] || '未知老師';
  }

  getMockTeacherId(teacherName) {
    const teacherIds = {
      '王老師': 'T001', '李老師': 'T002', '張老師': 'T003', '陳老師': 'T004',
      '劉老師': 'T005', '黃老師': 'T006', '林老師': 'T007', '吳老師': 'T008',
      '鄭老師': 'T009', '周老師': 'T010', '蔡老師': 'T011', '許老師': 'T012',
      '謝老師': 'T013', '羅老師': 'T014'
    };
    return teacherIds[teacherName] || 'T000';
  }

  getMockClassroom(subject) {
    const classroomMap = {
      '中國語文': 'A101', '英國語文': 'A102', '數學': 'A103', '通識教育': 'A104',
      '物理': 'B201', '化學': 'B202', '生物': 'B203', '歷史': 'C301',
      '地理': 'C302', '經濟': 'C303', '體育': '體育館', '音樂': '音樂室',
      '視覺藝術': '美術室', '設計與應用科技': '工藝室'
    };
    return classroomMap[subject] || 'A100';
  }

  getMockHomeworkTitle(subject, type) {
    const titleMap = {
      '練習': `${subject}練習題`,
      '作文': `${subject}作文`,
      '實驗報告': `${subject}實驗報告`,
      '研究報告': `${subject}研究報告`,
      '背誦': `${subject}背誦`,
      '繪圖': `${subject}繪圖作業`
    };
    return titleMap[type] || `${subject}作業`;
  }

  getMockHomeworkDescription(subject, type) {
    const descriptionMap = {
      '練習': `完成${subject}課本第X章練習題1-20題`,
      '作文': `以指定題目寫一篇${subject}作文，字數不少於500字`,
      '實驗報告': `完成${subject}實驗並撰寫實驗報告，包括數據分析和結論`,
      '研究報告': `完成${subject}專題研究報告，包括資料搜集和分析`,
      '背誦': `背誦${subject}指定章節內容，下週檢查`,
      '繪圖': `完成${subject}相關繪圖作業，注意細節和創意`
    };
    return descriptionMap[type] || `完成${subject}指定作業`;
  }
}

// Create and export service instance
export const homeworkService = new HomeworkService();
export { HomeworkService };
